/**
 * useCurrencies Hook
 * 
 * Fetches currencies data and subscribes to changes.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { auth, db } from "../../../firebase";
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
  Timestamp,
  doc,
} from "firebase/firestore";
import { Currency } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { sortCurrenciesByOrder } from "../../../utils/currencyOrdering";

export interface UseCurrenciesParams {
  workspaceId: string;
  defaultCurrency: string;
}

export interface UseCurrenciesResult {
  currencies: Currency[];
  loading: boolean;
  setCurrencies: React.Dispatch<React.SetStateAction<Currency[]>>;
}

/**
 * Hook for fetching currencies data
 */
export const useCurrencies = ({ workspaceId, defaultCurrency }: UseCurrenciesParams): UseCurrenciesResult => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currencyOrder, setCurrencyOrder] = useState<string[]>([]);

  // Fetch currency order from workspace settings
  useEffect(() => {
    if (!workspaceId) return;

    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCurrencyOrder(data.currencyOrder || []);
      } else {
        setCurrencyOrder([]);
      }
    });

    return () => {
      unsubscribeSettings();
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || (!auth.currentUser && !isDemoMode())) {
      setLoading(false);
      setCurrencies([]);
      return;
    }

    setLoading(true);

    const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
    
    // Use basic query without orderBy to avoid index issues
    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      try {
        if (snapshot.empty) {
          setCurrencies([]);
          setLoading(false);
          return;
        }

        const currenciesList: Currency[] = snapshot.docs
          .map((docSnap: DocumentSnapshot) => {
            const data = docSnap.data();
            
            if (!data) {
              return null;
            }
            
            // Get rate for current default currency
            const rates = (data.rates as Record<string, number | null>) || {};
            let currentRate: number | null;
            if (rates[defaultCurrency] !== undefined) {
              currentRate = rates[defaultCurrency];
            } else if (typeof data.rate === "number") {
              currentRate = data.rate;
            } else if (data.rate === null || data.rate === undefined) {
              currentRate = null;
            } else {
              const parsed = parseFloat(String(data.rate));
              currentRate = isNaN(parsed) ? null : parsed;
            }
            
            const currency: Currency = {
              code: docSnap.id,
              name: (data.name as string) || "",
              symbol: (data.symbol as string) || "",
              rate: currentRate,
              rates: rates,
              manuallyEdited: data.manuallyEdited === true,
              decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
              updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
            };
            
            return currency;
          })
          .filter((currency): currency is Currency => currency !== null)
          .filter((currency) => currency.code !== defaultCurrency); // Filter out default currency
        
        // Sort according to workspace currency order
        const sortedCurrencies = sortCurrenciesByOrder(currenciesList, currencyOrder, defaultCurrency);
        setCurrencies(sortedCurrencies);
        setLoading(false);
      } catch (error) {
        console.error("Error processing currency snapshot:", error);
        setLoading(false);
        setCurrencies([]);
      }
    };

    // Start with basic query (no orderBy) to avoid index issues
    const basicQuery = query(currenciesRef);
    
    const unsubscribe = onSnapshot(
      basicQuery,
      handleSnapshot,
      (error: FirestoreError) => {
        console.error("Error fetching currencies:", error);
        
        // Try a direct getDocs as fallback
        getDocs(currenciesRef)
          .then((snapshot) => {
            handleSnapshot(snapshot);
          })
          .catch((fallbackError) => {
            console.error("Fallback getDocs also failed:", fallbackError);
            setLoading(false);
            setCurrencies([]);
          });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [workspaceId, defaultCurrency, currencyOrder]);

  return {
    currencies,
    loading,
    setCurrencies,
  };
};
